import { makeStyles } from '@material-ui/styles'
import ListTableField from 'core/components/validatedForm/ListTableField'
import ModalForm from 'core/elements/modal/ModalForm'
import Text from 'core/elements/Text'
import useListAction from 'core/hooks/useListAction'
import useParams from 'core/hooks/useParams'
import useSelectorWithParams from 'core/hooks/useSelectorWithParams'
import useUpdateAction from 'core/hooks/useUpdateAction'
import Theme from 'core/themes/model'
import ServiceUnhealthyInfo from 'openstack/components/common/ServiceUnhealthyInfo'
import { listStatewiseVirtualMachinesLite } from 'openstack/components/vms/actions'
import { vmListSelectorForAttachVolume } from 'openstack/components/vms/selectors'
import React, { useCallback } from 'react'
import { attachVolume } from './actions'

const useStyles = makeStyles<Theme>((theme) => ({
  fields: {
    gap: 16,
    display: 'grid',
  },
}))

const vmColumns = [
  {
    id: 'name',
    label: 'Available VMs',
    width: 'medium',
  },
  {
    id: 'state',
    label: 'State',
  },
]

export default function AttachVolumeDialog({ rows: [volume], onClose }) {
  const classes = useStyles()
  const defaultParams = {
    vm: null,
  }
  const { params, getParamsUpdater, setParams } = useParams(defaultParams)
  const { loading: loadingVms } = useListAction(listStatewiseVirtualMachinesLite, {
    params: {},
  })
  const vmsList = useSelectorWithParams(vmListSelectorForAttachVolume, {})
  const { update, updating, error, reset } = useUpdateAction(attachVolume)

  const handleSubmit = useCallback(async () => {
    reset()
    const vm = params.vm[0]
    const body = {
      volumeAttachment: {
        volumeId: volume.id,
      },
    }
    const { error } = await update({ id: volume.id, vmId: vm.id, body })
    if (!error) handleClose()
  }, [params, volume, update, reset])

  const handleClose = () => {
    setParams(defaultParams)
    reset()
    onClose()
  }

  return (
    <ModalForm
      title="Attach Volume"
      onSubmit={handleSubmit}
      onClose={onClose}
      submitting={updating}
      error={error}
      submitTitle={`Attach Volume`}
      panel="dialog"
      maxWidth={1200}
      open
    >
      <ServiceUnhealthyInfo action="Volume Attachment" hypervisor blockStorage />
      <div className={classes.fields}>
        <Text variant="body2">Select the VM that you would like to attach the volume to.</Text>
        <Text variant="body2">
          You can attach a volume to at most one VM. Attaching a volume to a VM makes it accessible
          to that VM as an independent disk.
        </Text>
        <Text variant="body2">
          <b>NOTE</b>: A VM must be in <b>active</b>, <b>suspended</b>, or <b>stopped</b> state
          before you can attach a volume to it. Only VMs in these states are listed below.
        </Text>
        <ListTableField
          id="vm"
          data={vmsList}
          loading={loadingVms}
          columns={vmColumns}
          onChange={getParamsUpdater('vm')}
          value={params.vm}
          uniqueIdentifier="id"
          searchTargets={['name', 'id']}
          required
        />
      </div>
    </ModalForm>
  )
}
